/**
 * PATH: packages/bills/src/supplierOption.ts
 * FILE_ID: BILLS-PKG-SUPPLIEROPTION-001
 * PURPOSE: Pure helpers for the bill Vendor supplier-picker — normalise the
 *          (paginated or array) /api/quotebuilder/suppliers/ response into
 *          BillSupplierOption[], and map a Supplier.source enum to a short
 *          UI badge label. Shared by both BillsClient impls + the picker so
 *          the shape/labels can't drift, and is unit-tested in isolation.
 */
import type { BillSupplierOption } from './types';

/** One raw supplier row → BillSupplierOption (defensive string coercion). */
export function normalizeSupplierOption(r: Record<string, unknown>): BillSupplierOption {
  return {
    id: String(r.id),
    name: String(r.name ?? ''),
    code: r.code != null ? String(r.code) : undefined,
    source: r.source != null ? String(r.source) : undefined,
    email: (r.email as string) || null,
    xero_contact_id: (r.xero_contact_id as string) || null,
  };
}

/**
 * Normalise the suppliers endpoint payload — tolerates both a bare array and
 * DRF's ``{ results: [...] }`` pagination wrapper — into BillSupplierOption[].
 */
export function normalizeSupplierList(data: unknown): BillSupplierOption[] {
  const rows = Array.isArray(data)
    ? data
    : ((data as { results?: unknown[] } | null)?.results ?? []);
  return (rows as Array<Record<string, unknown>>).map(normalizeSupplierOption);
}

/** Short provenance label for the source badge (null = no badge). */
export function supplierSourceLabel(source?: string | null): string | null {
  if (!source) return null;
  const map: Record<string, string> = {
    xero: 'Xero',
    email: 'Email',
    manual: 'Manual',
    import: 'Imported',
    simpro: 'Simpro',
  };
  return map[source] ?? source;
}
